import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import VaccinationFiltersHeader from '../Header/VaccinationFiltersHeader';
import ActionTableButton from '../Buttons/ActionTableButton';
import VaccinationKPI from '../Header/VaccinationKPI';
import { Link } from 'react-router-dom';import vaccinationService from '../services/vaccinationService'; // Import the service
import { TextField, IconButton, Chip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// Main component to display a table of vaccinations
const VaccinationTable = ({ vaccinations: propVaccinations = [], onDeleteVaccinations, onStatusChange }) => {
  
  // Base states for the component
  const [vaccinations, setVaccinations] = useState([]); // List of processed vaccinations
  const [searchTerm, setSearchTerm] = useState(''); // General search term
  const [rowSelection, setRowSelection] = useState({}); // Control of selected rows
  const [isLoading, setIsLoading] = useState(true); // Loading indicator
  const [globalFilter, setGlobalFilter] = useState(''); // Global filter for MaterialReactTable
  const [activeFilters, setActiveFilters] = useState({}); // Active filters for highlighting
  
  // State to maintain original vaccinations (without transformation)
  const [originalVaccinations, setOriginalVaccinations] = useState([]);
  // State to maintain filtered vaccinations in original format
  const [filteredOriginalVaccinations, setFilteredOriginalVaccinations] = useState([]);
  
  // State for cell editing
  const [editingCell, setEditingCell] = useState(null); // {rowId, columnId, value}
  const [editValue, setEditValue] = useState('');

  // Transforms API data to the format expected by the table
  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    // Log original data for debugging
    console.log('Original vaccinations:', apiData.length);
    
    const transformedData = apiData.map(vaccination => ({
      id: vaccination._id,
      vaccineId: vaccination.vaccineId || '',
      vaccineName: vaccination.vaccineName || '',
      patientId: vaccination.patientId || '',
      dateAdministered: vaccination.dateAdministered ? new Date(vaccination.dateAdministered).toISOString().split('T')[0] : '',
      nextDueDate: vaccination.nextDueDate ? new Date(vaccination.nextDueDate).toISOString().split('T')[0] : '',
      doseNumber: vaccination.doseNumber || 0,
      administrationRoute: vaccination.administrationRoute || '',
      administrationSite: vaccination.administrationSite || '',
      lotNumber: vaccination.lotNumber || '',
      manufacturerId: vaccination.manufacturerId || '',
      expirationDate: vaccination.expirationDate ? new Date(vaccination.expirationDate).toISOString().split('T')[0] : '',
      administratorId: vaccination.administratorId || '',
      status: vaccination.status || 'Scheduled',
      notes: vaccination.notes || '',
      vaccinationId: vaccination.vaccinationId || '',
      isActive: vaccination.isActive !== undefined ? vaccination.isActive : true, // Add isActive field with default true
      createdAt: vaccination.createdAt ? new Date(vaccination.createdAt).toISOString().split('T')[0] : '',
      updatedAt: vaccination.updatedAt ? new Date(vaccination.updatedAt).toISOString().split('T')[0] : ''
    }));
    
    // Log transformed data for debugging
    console.log('Transformed vaccinations:', transformedData.length);
    return transformedData;
  };
  
  // Effect to transform and load vaccinations from props
  useEffect(() => {
    if (propVaccinations.length > 0) {
      // Save original vaccinations
      setOriginalVaccinations(propVaccinations);
      // Initially filtered vaccinations are all vaccinations
      setFilteredOriginalVaccinations(propVaccinations);
      
      // Transform API data
      const transformedData = transformApiData(propVaccinations);
      setVaccinations(transformedData);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setVaccinations([]);
      setOriginalVaccinations([]);
      setFilteredOriginalVaccinations([]);
    }
  }, [propVaccinations]);

  // Get selected vaccinations from the rowSelection state
  const getSelectedVaccinations = () => {
    const selectedRows = Object.keys(rowSelection).filter(key => rowSelection[key]);
    return vaccinations.filter((_, index) => selectedRows.includes(index.toString()));
  };

  // Function to filter vaccinations (for VaccinationFiltersHeader)
  const handleFilterChange = (filters) => {
    // Store active filters for highlighting
    setActiveFilters(filters);
    
    // Get base data to filter
    const baseData = propVaccinations.length > 0 ? 
      transformApiData(propVaccinations) : [];
    
    const filtered = baseData.filter(vaccination => {
      return (
        // Match original fields
        (!filters.id || (vaccination.id && vaccination.id.toString().toLowerCase().includes(filters.id.toLowerCase()))) &&
        (!filters.vaccineId || (vaccination.vaccineId && vaccination.vaccineId.toString().toLowerCase().includes(filters.vaccineId.toLowerCase()))) &&
        (!filters.vaccineName || (vaccination.vaccineName && vaccination.vaccineName.toString().toLowerCase().includes(filters.vaccineName.toLowerCase()))) &&
        (!filters.patientId || (vaccination.patientId && vaccination.patientId.toString().toLowerCase().includes(filters.patientId.toLowerCase()))) &&
        (!filters.dateAdministered || (vaccination.dateAdministered && vaccination.dateAdministered.includes(filters.dateAdministered))) &&
        (!filters.nextDueDate || (vaccination.nextDueDate && vaccination.nextDueDate.includes(filters.nextDueDate))) &&
        (!filters.doseNumber || (vaccination.doseNumber && vaccination.doseNumber.toString().includes(filters.doseNumber.toString()))) &&
        (!filters.administrationRoute || (vaccination.administrationRoute && vaccination.administrationRoute.toString().toLowerCase().includes(filters.administrationRoute.toLowerCase()))) &&
        (!filters.administrationSite || (vaccination.administrationSite && vaccination.administrationSite.toString().toLowerCase().includes(filters.administrationSite.toLowerCase()))) &&
        (!filters.lotNumber || (vaccination.lotNumber && vaccination.lotNumber.toString().toLowerCase().includes(filters.lotNumber.toLowerCase()))) &&
        (!filters.manufacturerId || (vaccination.manufacturerId && vaccination.manufacturerId.toString().toLowerCase().includes(filters.manufacturerId.toLowerCase()))) &&
        (!filters.expirationDate || (vaccination.expirationDate && vaccination.expirationDate.includes(filters.expirationDate))) &&
        (!filters.administratorId || (vaccination.administratorId && vaccination.administratorId.toString().toLowerCase().includes(filters.administratorId.toLowerCase()))) &&
        (!filters.status || (vaccination.status && vaccination.status.toString().toLowerCase().includes(filters.status.toLowerCase()))) &&
        (!filters.notes || (vaccination.notes && vaccination.notes.toString().toLowerCase().includes(filters.notes.toLowerCase()))) &&
        (!filters.vaccinationId || (vaccination.vaccinationId && vaccination.vaccinationId.toString().toLowerCase().includes(filters.vaccinationId.toLowerCase()))) &&
        // Filter for active status if provided
        (filters.isActive === undefined || vaccination.isActive === filters.isActive)
      );
    });

    setVaccinations(filtered);
    
    // Important: Update filtered original vaccinations for KPIs
    // Get a unique set of filtered vaccination IDs
    const filteredIds = new Set(filtered.map(item => item.id));
    const filteredOriginal = originalVaccinations.filter(vaccination => 
      filteredIds.has(vaccination._id)
    );
    setFilteredOriginalVaccinations(filteredOriginal);
    
    // Reset selection when filters change
    setRowSelection({});
  };

  // Universal search (for VaccinationFiltersHeader and table filter)
  const handleUniversalSearch = (term) => {
    setSearchTerm(term);
    setGlobalFilter(term); // Use globalFilter from MaterialReactTable
    
    // If there's a search term, apply a basic filter on vaccinations
    if (term) {
      const termLower = term.toLowerCase();
      const baseData = propVaccinations.length > 0 ? 
        transformApiData(propVaccinations) : [];
      
      const searchFiltered = baseData.filter(vaccination => {
        // Search in all text fields
        return Object.entries(vaccination).some(([key, value]) => {
          // Only search in string properties
          return typeof value === 'string' && value.toLowerCase().includes(termLower);
        });
      });
      
      setVaccinations(searchFiltered);
      
      // Update filtered original vaccinations for KPIs
      const filteredIds = new Set(searchFiltered.map(item => item.id));
      const filteredOriginal = originalVaccinations.filter(vaccination => 
        filteredIds.has(vaccination._id)
      );
      setFilteredOriginalVaccinations(filteredOriginal);
    } else {
      // If no term, show all vaccinations
      const transformedData = transformApiData(propVaccinations);
      setVaccinations(transformedData);
      setFilteredOriginalVaccinations(originalVaccinations);
    }
    
    // Reset selection when search changes
    setRowSelection({});
  };

  // Function to handle changes in the table's global filter
  const handleGlobalFilterChange = (newFilter) => {
    setGlobalFilter(newFilter);
    
    // If there are changes in the global filter through the table, also
    // update KPIs consistently
    if (newFilter !== searchTerm) {
      handleUniversalSearch(newFilter);
    }
  };

  // Function to handle active status toggle with auto-refresh
  const handleStatusChange = async (id, newStatus) => {
    try {
      // Call the service method to update the status
      await vaccinationService.updateVaccinationActiveStatus(id, newStatus);
      
      // Notify parent component if callback is provided
      if (onStatusChange) {
        onStatusChange(id, newStatus);
      }
      
      console.log(`Vaccination ${id} status updated to ${newStatus ? 'active' : 'inactive'}`);
      
      // Refresh the page automatically after status change
      window.location.reload();
    } catch (error) {
      console.error(`Error updating vaccination status:`, error);
      // Handle error (could show a notification to the user)
    }
  };

  // Function to update data after deletion
  const refreshData = (removedItem = null) => {
    if (removedItem) {
      const updatedVaccinations = vaccinations.filter(v => v.id !== removedItem.id);
      setVaccinations(updatedVaccinations);
      
      // Also update original vaccinations
      const updatedOriginal = originalVaccinations.filter(v => v._id !== removedItem.id);
      setOriginalVaccinations(updatedOriginal);
      
      // Update filtered vaccinations
      const updatedFilteredOriginal = filteredOriginalVaccinations.filter(v => v._id !== removedItem.id);
      setFilteredOriginalVaccinations(updatedFilteredOriginal);
      
      // Notify parent component about deletion
      if (onDeleteVaccinations) {
        onDeleteVaccinations(removedItem);
      }
      
      // Reset selection after deletion
      setRowSelection({});
    }
  };

  // Handler for row click
  const handleRowClick = (row) => {
    console.log('Selected vaccination:', row.original);
    // You can add additional logic for row selection here
  };

  // Function to highlight text based on search terms with complete validations
  const highlightText = (text, filterTerm, fieldName) => {
    // Step 1: Verify if the text is undefined or null
    if (text === undefined || text === null) {
      return '';
    }
    
    // Step 2: Convert text to string if it's not
    const textStr = typeof text === 'string' ? text : String(text);
    
    // Step 3: Get the filter term with validations
    let termToHighlight = '';
    if (fieldName && activeFilters && activeFilters[fieldName]) {
      termToHighlight = activeFilters[fieldName].toLowerCase();
    } else if (searchTerm) {
      termToHighlight = searchTerm.toLowerCase();
    }
    
    // Step 4: Check if there's a filter term or global filter
    const hasGlobalFilter = globalFilter !== undefined && globalFilter !== null && globalFilter !== '';
    if (!termToHighlight && !hasGlobalFilter) {
      return textStr;
    }
    
    // Step 5: Use the global filter if there's no specific term
    let term = termToHighlight;
    if (!term && hasGlobalFilter) {
      term = globalFilter.toLowerCase();
    }
    
    // Step 6: Final check before splitting
    if (!term || !textStr || !textStr.toLowerCase().includes(term)) {
      return textStr;
    }
    
    // Step 7: Split the text and highlight matches
    try {
      const parts = textStr.split(new RegExp(`(${term})`, 'gi'));
      
      return (
        <>
          {parts.map((part, index) => {
            if (!part) return null;
            return part.toLowerCase() === term.toLowerCase() ? 
              <span key={index} style={{ backgroundColor: '#FFFF00', fontWeight: 'bold' }}>{part}</span> : 
              part;
          })}
        </>
      );
    } catch (error) {
      console.error("Error highlighting text:", error);
      return textStr; // Return original text in case of error
    }
  };

  // Function to handle double click on a cell to start editing
  const handleCellDoubleClick = (cell, row) => {
    // Check if the cell is editable
    if (isEditableField(cell.column.id)) {
      // Only process if we're not already editing
      if (editingCell === null) {
        setEditingCell({
          rowId: row.index,
          columnId: cell.column.id,
          value: cell.getValue() || ''
        });
        setEditValue(cell.getValue() || '');
      }
    }
  };

  // Function to determine if a field is editable
  const isEditableField = (columnId) => {
    // List of editable fields
    const editableFields = [
      'administrationRoute',
      'administrationSite',
      'lotNumber',
      'manufacturerId',
      'status',
      'notes',
      'doseNumber'
    ];
    
    return editableFields.includes(columnId);
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Function to save edited cell value to API
  const handleSaveEdit = async () => {
    try {
      if (!editingCell) return;
      
      // Get the current row data
      const row = vaccinations[editingCell.rowId];
      if (!row) return;
      
      // Find the original vaccination in originalVaccinations
      const vaccinationId = row.id;
      const originalVaccination = originalVaccinations.find(v => v._id === vaccinationId);
      
      if (!originalVaccination) {
        console.error('Original vaccination not found');
        return;
      }
      
      // Prepare data for API update
      const updateData = {
        [editingCell.columnId]: editValue
      };
      
      // Call API to update vaccination
      await vaccinationService.updateVaccination(vaccinationId, updateData);
      
      // Update local state
      setVaccinations(prevVaccinations => {
        const newVaccinations = [...prevVaccinations];
        newVaccinations[editingCell.rowId] = {
          ...newVaccinations[editingCell.rowId],
          [editingCell.columnId]: editValue
        };
        return newVaccinations;
      });
      
      // Reset editing state
      setEditingCell(null);
      setEditValue('');
      
      console.log(`Updated vaccination ${vaccinationId}, field: ${editingCell.columnId}, value: ${editValue}`);
      
      // Optional: Show success message to user
    } catch (error) {
      console.error('Error updating vaccination:', error);
      // Optional: Show error message to user
    }
  };

  // Custom cell renderer that handles edit mode
  const renderCell = (cell, row) => {
    const isEditing = 
      editingCell && 
      editingCell.rowId === row.index && 
      editingCell.columnId === cell.column.id;
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            variant="standard"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            size="small"
            autoFocus
            fullWidth
            onClick={(e) => e.stopPropagation()} // Prevent row selection
          />
          <IconButton 
            size="small" 
            color="primary" 
            onClick={(e) => {
              e.stopPropagation();
              handleSaveEdit();
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="secondary" 
            onClick={(e) => {
              e.stopPropagation();
              handleCancelEdit();
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </div>
      );
    }
    
    // Return the normal cell content with highlighting when not editing
    return highlightText(cell.getValue(), 
      cell.column.id in activeFilters ? activeFilters[cell.column.id] : null, 
      cell.column.id);
  };

  // Function to render status with color coding
  const renderStatus = (status) => {
    const getStatusColor = (status) => {
      switch (status?.toLowerCase()) {
        case 'administered':
          return 'success';
        case 'scheduled':
          return 'primary';
        case 'missed':
          return 'error';
        case 'cancelled':
          return 'secondary';
        default:
          return 'default';
      }
    };

    return (
      <Chip 
        label={status || 'Unknown'} 
        color={getStatusColor(status)}
        size="small"
        variant="outlined"
      />
    );
  };

  // Function to check if vaccination is due soon or overdue
  const getDueDateStatus = (nextDueDate) => {
    if (!nextDueDate) return { text: '-', color: 'default' };
    
    const today = new Date();
    const dueDate = new Date(nextDueDate);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: `Missed ${Math.abs(diffDays)} days ago`, color: 'error' };
    } else if (diffDays <= 7) {
      return { text: `In ${diffDays} Days`, color: 'warning' };
    } else {
      return { text: nextDueDate, color: 'default' };
    }
  };

  // Column definitions for MaterialReactTable with highlighted text
  const columns = useMemo(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 0,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <ActionTableButton 
            item={row.original}
            refreshData={() => refreshData(row.original)}
          />          
        ),
      },
      {
        accessorKey: 'vaccineId',
        header: 'Vacc. Id',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'vaccineName',
        header: 'Vaccine Name',
        size: 180,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'patientId',
        header: 'Patient Id',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'dateAdministered',
        header: 'Date Administered',
        size: 140,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'nextDueDate',
        header: 'Next Due Date',
        size: 140,
        Cell: ({ cell, row }) => {
          const dueDateStatus = getDueDateStatus(cell.getValue());
          return (
            <span style={{ color: dueDateStatus.color === 'error' ? 'red' : 
                                   dueDateStatus.color === 'warning' ? 'orange' : 'inherit' }}>
              {highlightText(dueDateStatus.text, 
                cell.column.id in activeFilters ? activeFilters[cell.column.id] : null, 
                cell.column.id)}
            </span>
          );
        },
      },
      {
        accessorKey: 'doseNumber',
        header: 'Dose #',
        size: 80,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        Cell: ({ cell, row }) => {
          const isEditing = 
            editingCell && 
            editingCell.rowId === row.index && 
            editingCell.columnId === cell.column.id;
          
          if (isEditing) {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  variant="standard"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  size="small"
                  autoFocus
                  fullWidth
                  onClick={(e) => e.stopPropagation()}
                />
                <IconButton 
                  size="small" 
                  color="primary" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveEdit();
                  }}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="secondary" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </div>
            );
          }
          
          return renderStatus(cell.getValue());
        },
      },
      {
        accessorKey: 'administrationRoute',
        header: 'Route',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'administrationSite',
        header: 'Site',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'lotNumber',
        header: 'Lot Number',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'manufacturerId',
        header: 'Manufacturer',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'expirationDate',
        header: 'Exp. Date',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'administratorId',
        header: 'Administrator',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        size: 250,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
    ],
    [activeFilters, searchTerm, globalFilter, editingCell, editValue],
  );

  return (
    <div className="container-fluid px-lg-5">
      {/* Breadcrumb */}
                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Vaccinations</li>
                            </ol>
                        </nav>
      {/* KPI component with original and filtered data */}
      <VaccinationKPI 
        vaccinations={originalVaccinations} 
        filteredVaccinations={filteredOriginalVaccinations} 
      />
      
      {/* Header component with table filters */}
      <VaccinationFiltersHeader 
        onFilterChange={handleFilterChange} 
        onSearchChange={handleUniversalSearch}
        selectedVaccinations={getSelectedVaccinations()}
        onClearSelection={() => setRowSelection({})}
        onBulkDelete={(vaccinationsToDelete) => {
          // Remove selected vaccinations from local state
          const remainingVaccinations = vaccinations.filter(
            vaccination => !vaccinationsToDelete.some(v => v.id === vaccination.id)
          );
          
          setVaccinations(remainingVaccinations);
          
          // Also update original vaccinations
          const deletedIds = new Set(vaccinationsToDelete.map(v => v.id));
          const updatedOriginal = originalVaccinations.filter(v => !deletedIds.has(v._id));
          setOriginalVaccinations(updatedOriginal);
          
          // Update filtered vaccinations
          const updatedFilteredOriginal = filteredOriginalVaccinations.filter(v => !deletedIds.has(v._id));
          setFilteredOriginalVaccinations(updatedFilteredOriginal);
          
          // Notify parent component about bulk deletions - pass the full array
          if (onDeleteVaccinations && vaccinationsToDelete.length > 0) {
            onDeleteVaccinations(vaccinationsToDelete); // Pass the complete array
          }
          
          setRowSelection({});
        }}
      />

      {/* Main table with MaterialReactTable */}
      <MaterialReactTable
        columns={columns}
        data={vaccinations}
        state={{ 
          isLoading,
          rowSelection,
          globalFilter
        }}
        enableRowSelection
        onRowSelectionChange={setRowSelection}
        enableColumnFilters
        enableColumnOrdering
        enableSorting
        enablePagination
        enableGlobalFilter
        onGlobalFilterChange={handleGlobalFilterChange}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => handleRowClick(row),
          onDoubleClick: (event) => {
            // Find which cell was double-clicked
            const cellElement = event.target.closest('td');
            if (cellElement) {
              const cellIndex = Array.from(cellElement.parentNode.children).indexOf(cellElement);
              // Map cell index to column
              const column = columns[cellIndex];
              if (column) {
                handleCellDoubleClick(
                  { column, getValue: () => row.original[column.accessorKey] }, 
                  row
                );
              }
            }
          },
          sx: { 
            cursor: 'pointer',
            // backgroundColor: row.original.isActive ? 'rgba(144, 238, 144, 0.13)' : 'rgba(255, 182, 193, 0.2)',
          },
        })}
        muiTableBodyCellProps={({ cell }) => ({
          onDoubleClick: (event) => {
            event.stopPropagation();
            handleCellDoubleClick(cell, cell.row);
          },
          // Add visual indication for editable cells
          sx: isEditableField(cell.column.id) ? {
            position: 'relative',
            '&:hover::after': {
              content: '"✎"',
              position: 'absolute',
              top: '2px',
              right: '2px',
              fontSize: '10px',
              color: '#666',
            }
          } : {}
        })}
        initialState={{
          density: 'compact',
          pagination: { pageIndex: 0, pageSize: 10 }
        }}
        // Handle filtering in each column to also update KPIs
        muiTableHeadCellFilterTextFieldProps={{
          onChange: (e) => {
            // When filtering by column, we also update the KPIs
            // using our custom filtering system
            const fieldName = e.target.name || '';
            const value = e.target.value || '';
            
            // Extract column name from field name if present
            const columnName = fieldName.replace('filter-', '');
            
            if (columnName && value !== '') {
              // Create a new filter object based on current filters
              const newFilters = { ...activeFilters, [columnName]: value };
              handleFilterChange(newFilters);
            } else if (columnName && activeFilters[columnName]) {
              // If a filter is cleared, remove it from the filter object
              const newFilters = { ...activeFilters };
              delete newFilters[columnName];
              handleFilterChange(newFilters);
            }
          }
        }}
      />
    </div>
  );
};

export default VaccinationTable;